import { useEffect, useState } from "react";
import { Clock, Sun, Moon, Zap } from "lucide-react";
import { cn } from "@/lib/utils";
import { useI18n } from "@/lib/i18n";
import { isLondonNyOverlap } from "@/lib/marketSessions";

/** Forex session windows in UTC (24-hour clock, Mon–Fri only). */
const SESSIONS = [
  { key: "sydney",   name: "Sydney",   flag: "🇦🇺", open: 22, close:  7 },  // 22 → 07 (crosses midnight)
  { key: "tokyo",    name: "Tokyo",    flag: "🇯🇵", open:  0, close:  9 },
  { key: "london",   name: "London",   flag: "🇬🇧", open:  8, close: 17 },
  { key: "newyork",  name: "New York", flag: "🇺🇸", open: 13, close: 22 },
];

/** Check if a UTC hour falls inside a session that may cross midnight. */
function isSessionOpen(session, utcHour, utcDay) {
  // Forex closed on Saturday (6) and most of Sunday until 22:00 UTC.
  if (utcDay === 6) return false;                          // Saturday
  if (utcDay === 0 && utcHour < 22) return false;          // Sunday before Sydney open
  if (utcDay === 5 && utcHour >= 22) return false;         // Friday close
  const { open, close } = session;
  if (open < close) return utcHour >= open && utcHour < close;
  return utcHour >= open || utcHour < close;               // crosses midnight
}

function pad(n) { return String(n).padStart(2, "0"); }
function fmtHM(sec) {
  const s = Math.max(0, Math.floor(sec));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = s % 60;
  return h > 0 ? `${h}:${pad(m)}:${pad(ss)}` : `${pad(m)}:${pad(ss)}`;
}

function secondsUntilHour(now, targetHour) {
  const target = new Date(now);
  target.setUTCHours(targetHour, 0, 0, 0);
  if (target <= now) target.setUTCDate(target.getUTCDate() + 1);
  return Math.floor((target - now) / 1000);
}

export default function MarketClock() {
  const { t, lang } = useI18n();
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  const utcHour = now.getUTCHours();
  const utcDay = now.getUTCDay();

  const sessions = SESSIONS.map(s => {
    const open = isSessionOpen(s, utcHour, utcDay);
    const remainingSec = open
      ? secondsUntilHour(now, s.close)
      : secondsUntilHour(now, s.open);
    return { ...s, open, remainingSec };
  });

  const anyOpen = sessions.some(s => s.open);
  const openCount = sessions.filter(s => s.open).length;
  const isNight = utcHour >= 21 || utcHour < 6;

  const localeCode = { ar: "ar-EG", en: "en-US", de: "de-DE", fr: "fr-FR", es: "es-ES", it: "it-IT", ru: "ru-RU", tr: "tr-TR", pt: "pt-BR", zh: "zh-CN", bg: "bg-BG" }[lang] || "en-US";
  const utcTime = now.toLocaleTimeString(localeCode, { timeZone: "UTC", hour: "2-digit", minute: "2-digit", second: "2-digit" });
  const localTime = now.toLocaleTimeString(localeCode, { hour: "2-digit", minute: "2-digit", second: "2-digit" });
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "Local";
  const inOverlap = isLondonNyOverlap(now);

  return (
    <section className="border-b border-zinc-900" data-testid="market-clock">
      {inOverlap && (
        <div
          data-testid="overlap-banner"
          className="relative overflow-hidden bg-gradient-to-r from-amber-500/15 via-orange-500/10 to-amber-500/15 border-b border-amber-500/30"
        >
          <div className="max-w-7xl mx-auto px-6 py-2.5 flex items-center justify-center gap-3 text-amber-400 text-xs font-bold">
            <Zap className="h-4 w-4 fill-amber-400 animate-pulse" />
            <span className="tracking-wide uppercase">{t("overlap_title")}</span>
            <span className="text-amber-500/70 font-normal">·</span>
            <span className="font-normal text-amber-300">{t("overlap_desc")}</span>
          </div>
        </div>
      )}
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center gap-6 flex-wrap">
        <div className="flex items-center gap-3">
          <div className={cn(
            "w-9 h-9 rounded-sm border flex items-center justify-center",
            anyOpen ? "bg-emerald-500/10 border-emerald-500/30" : "bg-zinc-800/60 border-zinc-700"
          )}>
            {isNight ? <Moon className="h-4 w-4 text-zinc-300" /> : <Sun className="h-4 w-4 text-amber-400" />}
          </div>
          <div>
            <div className="text-xs text-zinc-500 flex items-center gap-1.5">
              <Clock className="h-3 w-3" /> {t("market_hours")}
            </div>
            <div className="text-sm font-bold flex items-center gap-2">
              {anyOpen ? (
                <span className="text-emerald-400 flex items-center gap-1.5">
                  <span className="relative flex h-1.5 w-1.5">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-70" />
                    <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-400" />
                  </span>
                  {t("forex_open")}
                  <span className="text-zinc-500 font-normal">·</span>
                  <span className="num text-zinc-300 text-xs">{openCount} {t("sessions_active")}</span>
                </span>
              ) : (
                <span className="text-amber-400">{t("forex_closed")}</span>
              )}
              <span className="text-zinc-600 text-[10px] font-normal ms-2 num" dir="ltr">UTC {utcTime}</span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap ms-auto">
          {sessions.map(s => (
            <SessionPill key={s.key} session={s} label={t(`session_${s.key}`)} />
          ))}
          <CryptoPill label={t("crypto_always_open")} />
        </div>
      </div>
    </section>
  );
}

function SessionPill({ session, label }) {
  const { open, remainingSec } = session;
  return (
    <div
      data-testid={`session-${session.key}`}
      className={cn(
        "flex items-center gap-2 px-3 py-1.5 rounded-sm border text-[11px]",
        open
          ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-400"
          : "bg-zinc-900/60 border-zinc-800 text-zinc-500"
      )}
    >
      <span className="text-sm leading-none">{session.flag}</span>
      <span className="font-bold min-w-[52px]">{label}</span>
      <span className="text-zinc-600">·</span>
      <span className={cn("num font-mono", open ? "text-emerald-300" : "text-zinc-500")}>
        {open ? "−" : "→"}{fmtHM(remainingSec)}
      </span>
    </div>
  );
}

function CryptoPill({ label }) {
  return (
    <div
      data-testid="session-crypto"
      className="flex items-center gap-2 px-3 py-1.5 rounded-sm border text-[11px] bg-amber-500/10 border-amber-500/30 text-amber-400"
    >
      <span className="text-sm leading-none">₿</span>
      <span className="font-bold">{label}</span>
      <span className="text-amber-500/70">· 24/7</span>
    </div>
  );
}
